import clamp from "lodash/clamp";
import React from "react";
import { useEvent as useOnEvent } from "react-use";

import { abs } from "~/tools/abs";
import { cx } from "~/tools/class-variance";
import { differenceInMilliseconds, secondsToMilliseconds } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";
import { type Impulse, useScoped } from "~/tools/impulse";
import { min } from "~/tools/min";
import { timeFromPx } from "~/tools/time-from-px";
import { timeToPx } from "~/tools/time-to-px";
import { useHandler } from "~/tools/use-handler";

import { useTimelineContext, useTimelineRange } from "../_context";

import { TimelineControlElement } from "./TimelineControlElement";
import { TimelinePlayHead } from "./TimelinePlayHead";
import { useSetTimestampCursor } from "./useSetTimestampCursor";

const MINIMAL_BRUSH_INTERVAL_MS = secondsToMilliseconds(10);

export interface TimelineBrushAreaState {
	start: Time;
	end: Impulse<number>;
}

export interface BrushPosition {
	startPx: number;
	endPx: number;
}

export interface TimelineBrushAreaProps {
	state: TimelineBrushAreaState;
	areaOffsetTop?: number;
	areaOffsetBottom?: number;
	onBrush: (left: Time, right: Time, position: BrushPosition) => void;
	onCancel: VoidFunction;
}

export const TimelineBrushArea: React.FC<TimelineBrushAreaProps> = ({
	state,
	areaOffsetTop = 28,
	areaOffsetBottom = 8,
	onBrush,
	onCancel,
}) => {
	const { stageWidth, refreshInterval, formatTimestamp } = useTimelineContext();
	const [timelineStart, timelineEnd, now] = useTimelineRange({
		// if the interval is too long, the play head formatted time updates too rarely
		interval: min(300, refreshInterval),
	});
	const endPx = useScoped((scope) => clamp(state.end.getValue(scope), 0, stageWidth), [state.end, stageWidth]);

	const startPx = timeToPx({
		clamped: true,
		time: state.start,
		width: stageWidth,
		interval: { start: timelineStart, end: timelineEnd },
	});

	const endTime = timeFromPx({
		px: endPx,
		width: stageWidth,
		interval: { start: timelineStart, end: timelineEnd },
	});

	const isShort = abs(differenceInMilliseconds(endTime, state.start)) < MINIMAL_BRUSH_INTERVAL_MS;

	useSetTimestampCursor(endTime);

	useOnEvent(
		"mousemove",
		useHandler((event: MouseEvent) => {
			state.end.setValue(event.clientX);
		}),
	);

	useOnEvent(
		"mouseup",
		useHandler(() => {
			const [left, right] = endTime < state.start ? [endTime, state.start] : [state.start, endTime];
			const [start, end] = endPx < startPx ? [endPx, startPx] : [startPx, endPx];

			if (left !== right && !isShort) {
				onBrush(left, right, { startPx: start, endPx: end });
			} else {
				onCancel();
			}
		}),
	);

	return (
		<TimelineControlElement
			className="pointer-events-none inset-y-0"
			style={{
				width: abs(startPx - endPx),
			}}
			x={min(startPx, endPx)}
		>
			{/* colored glass */}
			<div
				className={cx("absolute inset-x-0", isShort ? "bg-red-500/20" : "bg-(--foundation-blue-600)/20")}
				style={{
					top: areaOffsetTop,
					bottom: areaOffsetBottom,
				}}
			/>

			{/* static play head */}
			{/* left-full should be used instead right-0 because it makes the positioning nicer */}
			<TimelinePlayHead
				className={cx("inset-y-0", startPx < endPx ? "left-0" : "left-full")}
				theme={isShort ? "red" : "default"}
			>
				{formatTimestamp({ time: state.start, timelineStart, timelineEnd, now }, startPx)}
			</TimelinePlayHead>

			{/* it is crucial to draw the 'end' part last, because it should always appear on top of the `start` one */}
			{/* left-full should be used instead right-0 because it makes the positioning nicer */}
			<TimelinePlayHead
				className={cx("inset-y-0", startPx <= endPx ? "left-full" : "left-0")}
				theme={isShort ? "red" : "default"}
			>
				{isShort ? "interval too small" : formatTimestamp({ time: endTime, timelineStart, timelineEnd, now }, endPx)}
			</TimelinePlayHead>
		</TimelineControlElement>
	);
};
